import { useCallback, useEffect, useState } from "react";
import wsManager from "../../utils/websocketManager";
import { forecastDataService } from "../../utils/services/forecast-data.service";

// Backstop for a missed WebSocket push while the insight is being written.
const POLL_INTERVAL_MS = 5000;
// After this long a "pending" insight is treated as failed, so the page never
// shows a placeholder forever.
const GIVE_UP_AFTER_MS = 10 * 60 * 1000;

const fromRun = (run) => ({
  status: run?.insight_status || (run?.insight || run?.explanation ? "ready" : "none"),
  insight: run?.insight || null,
  explanation: run?.explanation || null,
  error: run?.insight_error || null,
  // When the current attempt started: a regeneration, else the run itself.
  startedAt: run?.insight_requested_at || run?.createdAt || null,
});

// Latest insight state per run seen on this page, so a regenerated insight
// survives switching SKU tabs (the parent's copy of the run is still the old one).
const latestByRun = new Map();
const remember = (runId, state) => {
  if (runId) latestByRun.set(runId, state);
  return state;
};
// The stored run wins when it already has the insight; otherwise what this
// page last saw (e.g. a regeneration in progress, or one that just finished).
const initialFor = (run) => {
  const id = run?._id ? String(run._id) : null;
  const own = fromRun(run);
  const seen = id ? latestByRun.get(id) : null;
  return seen && own.status !== "ready" ? seen : own;
};

/** Test helper: forget what this page has seen. */
export const resetRunInsightMemory = () => latestByRun.clear();

/**
 * The run's SKU insight (explanation + insight HTML), kept live while the
 * insight workflow is still writing it (demand-forecast engine runs are
 * saved with insight_status "pending"). Updates in place from the
 * `forecastInsightReady` WebSocket push, backed up by polling the run.
 *
 * Returns { status: "none" | "pending" | "ready" | "error", insight,
 * explanation, error, startedAt, regenerate, regenerating }.
 * `regenerate()` re-runs only the insight (Updated Forecast runs); the result
 * is saved on the run and arrives here like the first one.
 */
export default function useRunInsight(run) {
  const runId = run?._id ? String(run._id) : null;
  const [state, setStateRaw] = useState(() => initialFor(run));
  const [regenerating, setRegenerating] = useState(false);
  const setState = useCallback(
    (next) =>
      setStateRaw((prev) => remember(runId, typeof next === "function" ? next(prev) : next)),
    [runId],
  );

  // Reset when a different run (or a newer copy of it) is shown. Keyed on the
  // fields that matter, not the object, so a parent that rebuilds the run
  // object on every render doesn't cause an update loop.
  useEffect(() => {
    setStateRaw(initialFor(run));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?._id, run?.insight_status, run?.insight, run?.explanation, run?.insight_error]);

  const pending = state.status === "pending";

  // Live push from the server when the workflow finishes.
  useEffect(() => {
    if (!runId) return undefined;
    const unsub = wsManager.on("forecastInsightReady", (payload) => {
      if (String(payload?.forecast_data_id) !== runId) return;
      setState((s) => ({
        status: payload.insight_status || "ready",
        insight: payload.insight || null,
        explanation: payload.explanation || null,
        error: payload.insight_error || null,
        startedAt: s.startedAt,
      }));
    });
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [runId, setState]);

  // Fallback: re-read the run while it's pending.
  useEffect(() => {
    if (!runId || !pending) return undefined;
    const startedAt = state.startedAt ? new Date(state.startedAt).getTime() : Date.now();
    let cancelled = false;
    const check = async () => {
      // Re-read first: the page may hold an old copy of the run (opened while
      // pending) whose insight has since finished or failed.
      try {
        const doc = (await forecastDataService.get(runId))?.data;
        if (!cancelled && doc && doc.insight_status && doc.insight_status !== "pending") {
          setState(fromRun(doc));
          return;
        }
      } catch (e) {
        // try again on the next tick
      }
      if (!cancelled && Date.now() - startedAt > GIVE_UP_AFTER_MS) {
        setState((s) =>
          s.status === "pending"
            ? { ...s, status: "error", error: "The insight is taking longer than expected." }
            : s,
        );
      }
    };
    check();
    const timer = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, pending, state.startedAt]);

  const regenerate = useCallback(async () => {
    if (!runId || regenerating) return;
    setRegenerating(true);
    try {
      const res = await forecastDataService.regenerateInsight(runId);
      setState((s) => ({
        ...s,
        status: "pending",
        error: null,
        startedAt: res?.insight_requested_at || new Date().toISOString(),
      }));
    } catch (e) {
      setState((s) => ({
        ...s,
        status: "error",
        error: e?.response?.data?.message || "The insight could not be restarted. Please try again.",
      }));
    } finally {
      setRegenerating(false);
    }
  }, [runId, regenerating, setState]);

  return { ...state, regenerate, regenerating };
}
